import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { AuthorizationsObject } from '@dat/angular-auth';
import { DatiqUserStateService } from '@dat/datiq-user';
import { snakeCase } from 'lodash';
import { filter, lastValueFrom, take, tap } from 'rxjs';
import {
    IIqUser,
    IIqUserCapabilities,
    IQ_USER_CAPABILITY_KEY_PREFIX,
    emptyIqUser,
} from '../models';
import { isNil, isEmpty } from 'ramda';

@Injectable({
    providedIn: 'root',
})
export class IqUserService {
    constructor(private readonly _userService: DatiqUserStateService) {}

    // NOTE: wasn't able to get this working without bringing in the existing DatiqUserStateService
    // trying to use the services from @dat/angular-auth directly caused a runtime error regarding
    // a missing provider and what appeared to be a circular dependency which I could not figure out
    loggedInUser(): Signal<IIqUser> {
        const userSignal: WritableSignal<IIqUser> = signal({ ...emptyIqUser });

        /*
         * @georgepe_dat -> To simplify this we should use lastValueFrom from rxjs and make it a promise
         */
        this._userService
            .getLoggedInUser$()
            .pipe(
                filter(user => !isNil(user) && !isEmpty(user)),
                take(1),
                tap((user) => {
                    userSignal.set(user);
                }),
            )
            .subscribe();

        return userSignal;
    }

    getLoggedInUser(): Promise<IIqUser> {
        return lastValueFrom(this._userService.getLoggedInUser$());
    }

    mapCapabilities(authorizations: AuthorizationsObject): IIqUserCapabilities {
        const permissions: string[] =
            this.mapAutorizationPermissions(authorizations);

        return permissions.reduce(
            (
                currentCapabilities: IIqUserCapabilities,
                permission: string,
            ): IIqUserCapabilities =>
                this.addCapability(permission, currentCapabilities),
            {},
        );
    }

    mapAutorizationPermissions(authorizations: AuthorizationsObject): string[] {
        return !!authorizations?.permissions?.length
            ? authorizations.permissions
            : [];
    }

    addCapability(
        permission: string,
        currentCapabilities: IIqUserCapabilities,
    ): IIqUserCapabilities {
        const capabilityKey: string = this.mapCapabilityKey(permission);

        return {
            ...currentCapabilities,
            [capabilityKey]: true,
        };
    }
    
    mapCapabilityKey(permission: string): string {
        return `${IQ_USER_CAPABILITY_KEY_PREFIX}${snakeCase(permission)}`;
    }
}
